import { Container, Sprite, type Texture } from 'pixi.js';
import LAYOUT from './layout.json';
import { LID_FRAMES, type LidPose, type RigPose } from '../diceMotion.js';
import { SpritePool } from '../pixi/spritePool.js';

/**
 * Thai Hi-Lo drawn from its real-3D render (scripts/art/render/dice.html): the pedestal and
 * dish, eighteen dice (three places in the dish, six faces each), four frames of the lid
 * opening on its hinge with their shadows, and a spinning coin. Every layer was rendered by
 * one camera and is placed by its box in the shared frame, so they compose back into the
 * picture they were cut from: the lid shuts on the dish rim, the dice lie on its floor.
 */

type Box = [number, number, number, number];
const BOXES = LAYOUT.boxes as unknown as Record<string, Box>;
export const FRAME = LAYOUT.frame;

const COIN_FRAMES = 8;
const dieName = (slot: number, value: number) => `die-s${slot}-v${value}`;

/** Every piece the stage draws: the rendered layers it places, plus two of light. */
export const DICE_PIECES = [
  'table',
  ...[0, 1, 2].flatMap((s) => [1, 2, 3, 4, 5, 6].map((v) => dieName(s, v))),
  ...Array.from({ length: LID_FRAMES }, (_, k) => `lid-${k}`),
  ...Array.from({ length: LID_FRAMES }, (_, k) => `lidshadow-${k}`),
  ...Array.from({ length: COIN_FRAMES }, (_, k) => `coin-${k}`),
  'sparkle',
  'glow',
] as const;
export type DicePiece = (typeof DICE_PIECES)[number];

function placed(tex: Texture, name: string): Sprite {
  const [x, y, w, h] = BOXES[name]!;
  const s = new Sprite(tex);
  s.position.set(x, y);
  s.width = w;
  s.height = h;
  return s;
}

interface Coin {
  sprite: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  gravity: number;
  frame: number;
  spin: number;
  life: number;
  maxLife: number;
}

export class DiceStage {
  /** In frame pixels; `fit` scales it into the canvas. */
  readonly root = new Container();
  /** What the shake carries: everything on the pedestal. */
  private readonly rig = new Container();
  private readonly dice: Sprite[][] = [];
  private readonly glows: Sprite[] = [];
  private readonly lids: Sprite[] = [];
  private readonly shadows: Sprite[] = [];
  private readonly fx = new Container();
  private readonly pool = new SpritePool(this.fx);
  private readonly coins: Coin[] = [];
  private readonly glint: Sprite;
  private readonly centre: { x: number; y: number };

  constructor(private readonly tex: Record<DicePiece, Texture>) {
    const [tx, ty, tw, th] = BOXES.table!;
    // The shake pivots on the dish's middle, so the set rocks instead of swinging from a corner.
    this.centre = { x: tx + tw / 2, y: ty + th * 0.45 };
    this.rig.pivot.set(this.centre.x, this.centre.y);
    this.rig.position.set(this.centre.x, this.centre.y);
    this.rig.addChild(placed(tex.table, 'table'));
    for (let k = 0; k < LID_FRAMES; k++) this.shadows.push(this.rig.addChild(placed(tex[`lidshadow-${k}` as DicePiece], `lidshadow-${k}`)));
    for (let slot = 0; slot < 3; slot++) {
      const [x, y, w, h] = BOXES[dieName(slot, 1)]!;
      const glow = new Sprite(tex.glow);
      glow.anchor.set(0.5);
      glow.position.set(x + w / 2, y + h / 2);
      glow.width = glow.height = w * 1.7;
      glow.blendMode = 'add';
      glow.alpha = 0;
      this.glows.push(this.rig.addChild(glow));
      const faces: Sprite[] = [];
      for (let v = 1; v <= 6; v++) {
        const s = new Sprite(tex[dieName(slot, v) as DicePiece]);
        const [bx, by, bw, bh] = BOXES[dieName(slot, v)]!;
        // Anchored at its foot, so a landing squash presses it into the dish floor.
        s.anchor.set(0.5, 1);
        s.position.set(bx + bw / 2, by + bh);
        s.width = bw;
        s.height = bh;
        s.alpha = 0;
        faces.push(this.rig.addChild(s));
      }
      this.dice.push(faces);
    }
    for (let k = 0; k < LID_FRAMES; k++) this.lids.push(this.rig.addChild(placed(tex[`lid-${k}` as DicePiece], `lid-${k}`)));
    this.glint = new Sprite(tex.sparkle);
    this.glint.anchor.set(0.5);
    this.glint.blendMode = 'add';
    this.glint.alpha = 0;
    this.rig.addChild(this.glint);
    this.root.addChild(this.rig, this.fx);
    this.setLid({ frame: 0, y: 0, alpha: 1 });
  }

  /** Scales the frame to cover the canvas width, keeping the dish in view. */
  fit(width: number, height: number): number {
    const k = Math.min(width / FRAME.width, height / FRAME.height);
    this.root.scale.set(k);
    this.root.position.set((width - FRAME.width * k) / 2, (height - FRAME.height * k) / 2);
    return k;
  }

  /** A frame point in canvas coordinates. */
  toCanvas(x: number, y: number): { x: number; y: number } {
    return { x: this.root.position.x + x * this.root.scale.x, y: this.root.position.y + y * this.root.scale.y };
  }

  setRig(p: RigPose): void {
    this.rig.position.set(this.centre.x + p.x, this.centre.y + p.y);
    this.rig.rotation = p.rotation;
    this.rig.scale.set(p.scale);
  }

  /**
   * Shows the rendered lid frame nearest a fractional one; its shadow follows. Never a blend of
   * two: two different angles of a solid lid half-transparent over each other read as a ghost.
   */
  setLid(p: LidPose): void {
    const shown = Math.round(Math.min(LID_FRAMES - 1, Math.max(0, p.frame)));
    // A lid carried away no longer shades the dish.
    const shade = p.alpha * Math.max(0, 1 + p.y / (FRAME.height * 0.15));
    for (let k = 0; k < LID_FRAMES; k++) {
      const [, y] = BOXES[`lid-${k}`]!;
      this.lids[k]!.y = y + p.y;
      this.lids[k]!.alpha = k === shown ? p.alpha : 0;
      this.shadows[k]!.alpha = k === shown ? shade : 0;
    }
  }

  /** The faces the round rolled, or none (hidden under the shut lid). */
  showDice(values: readonly number[] | null): void {
    this.dice.forEach((faces, slot) => faces.forEach((s, i) => { s.alpha = values && values[slot] === i + 1 ? 1 : 0; }));
  }

  settleDie(slot: number, dy: number, scaleX: number, scaleY: number): void {
    for (let v = 1; v <= 6; v++) {
      const s = this.dice[slot]![v - 1]!;
      const [bx, by, bw, bh] = BOXES[dieName(slot, v)]!;
      s.position.set(bx + bw / 2, by + bh + dy);
      s.width = bw * scaleX;
      s.height = bh * scaleY;
    }
  }

  setGlow(slot: number, alpha: number): void {
    this.glows[slot]!.alpha = alpha;
  }

  /** Where a die sits, in frame pixels: its face's middle. */
  dieCentre(slot: number): { x: number; y: number } {
    const [x, y, w, h] = BOXES[dieName(slot, 1)]!;
    return { x: x + w / 2, y: y + h * 0.4 };
  }

  /** A twinkle on the lid's crown, `t` 0..1 through one pass; 0 or 1 hides it. */
  setGlint(t: number): void {
    if (t <= 0 || t >= 1) {
      this.glint.alpha = 0;
      return;
    }
    const [x, y, w, h] = BOXES['lid-0']!;
    this.glint.position.set(x + w * (0.32 + 0.36 * t), y + h * (0.3 - 0.12 * Math.sin(t * Math.PI)));
    this.glint.width = this.glint.height = w * 0.16 * (0.6 + 0.4 * Math.sin(t * Math.PI));
    this.glint.alpha = Math.sin(t * Math.PI);
  }

  /** Spinning gold coins thrown up out of the dish; for reduced motion a few that fade in place. */
  burst(n: number, reduced: boolean): void {
    for (let i = 0; i < n; i++) {
      const frame = Math.floor(Math.random() * COIN_FRAMES);
      const sprite = this.pool.acquire(this.tex[`coin-${frame}` as DicePiece]);
      sprite.blendMode = 'normal';
      sprite.width = sprite.height = FRAME.height * (0.05 + Math.random() * 0.025);
      const from = this.dieCentre(i % 3);
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.0;
      const speed = reduced ? 0 : FRAME.height * (0.012 + Math.random() * 0.014);
      const c: Coin = {
        sprite,
        x: from.x + (Math.random() - 0.5) * FRAME.height * 0.1,
        y: from.y - (reduced ? Math.random() * FRAME.height * 0.15 : 0),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - (reduced ? 0 : FRAME.height * 0.006),
        gravity: reduced ? 0 : FRAME.height * 0.0009,
        frame,
        spin: reduced ? 0 : 0.35 + Math.random() * 0.4,
        life: 0,
        maxLife: 40 + Math.random() * 20,
      };
      sprite.position.set(c.x, c.y);
      this.coins.push(c);
    }
  }

  /** Advances the coins by `k` sixtieths of a second. */
  update(k: number): void {
    for (let i = this.coins.length - 1; i >= 0; i--) {
      const c = this.coins[i]!;
      c.life += k;
      if (c.life >= c.maxLife) {
        this.pool.release(c.sprite);
        this.coins[i] = this.coins[this.coins.length - 1]!;
        this.coins.pop();
        continue;
      }
      c.vy += c.gravity * k;
      c.x += c.vx * k;
      c.y += c.vy * k;
      c.frame = (c.frame + c.spin * k) % COIN_FRAMES;
      c.sprite.texture = this.tex[`coin-${Math.floor(c.frame)}` as DicePiece];
      c.sprite.position.set(c.x, c.y);
      c.sprite.alpha = Math.min(1, (1 - c.life / c.maxLife) * 2.5);
    }
  }
}
